import { isHomeRelativePath, type RunEnvironmentDataRoot } from "common";

/**
 * The Writable toggle of an agent data source (agent-5-5, spec 7). The server decides again on create
 * (a request under a read folder is refused), so this only spares the user a refusal.
 */
export const WRITABLE_TOGGLE_REASONS = {
  readOnlyFolder:
    "Only a folder marked read and write on this run environment can hold a writable data source.",
  foldersUnavailable:
    "The folder settings of this run environment could not be read. Only its owner can see them.",
} as const;

const normalized = (value: string): string => {
  const trimmed = value.trim().replace(/\/+$/, "");
  return trimmed === "" ? "/" : trimmed;
};

const contains = (candidate: string, root: string): boolean =>
  root === "/" || candidate === root || candidate.startsWith(`${root}/`);

/**
 * The most specific root containing `path`, as the server's longest match decides it. A `~` row
 * (agent-6-1) never matches: only the server knows the home of the user's agent that resolves it.
 */
export const longestDataRoot = (
  path: string,
  roots: readonly RunEnvironmentDataRoot[]
): RunEnvironmentDataRoot | null => {
  const candidate = normalized(path);
  if (!candidate.startsWith("/") || candidate.split("/").includes("..")) {
    return null;
  }
  let best: RunEnvironmentDataRoot | null = null;
  for (const root of roots) {
    if (isHomeRelativePath(root.path)) continue;
    const rootPath = normalized(root.path);
    if (!contains(candidate, rootPath)) continue;
    if (best === null || rootPath.length > normalized(best.path).length) {
      best = root;
    }
  }
  return best;
};

export interface WritableToggleView {
  visible: boolean;
  enabled: boolean;
  reason: string | null;
}

export const writableToggleView = (input: {
  onAgent: boolean;
  path: string;
  roots: readonly RunEnvironmentDataRoot[] | undefined;
  rootsLoading: boolean;
  rootsUnavailable: boolean;
}): WritableToggleView => {
  if (!input.onAgent) return { visible: false, enabled: false, reason: null };
  if (input.rootsLoading) {
    return { visible: true, enabled: false, reason: null };
  }
  if (input.rootsUnavailable || input.roots === undefined) {
    return {
      visible: true,
      enabled: false,
      reason: WRITABLE_TOGGLE_REASONS.foldersUnavailable,
    };
  }
  const match = longestDataRoot(input.path, input.roots);
  // A path no absolute folder covers may still lie in a read and write ~ folder, which only the
  // server can resolve (agent-6-1): leave that decision to the create, which refuses if it is not.
  const homeMayCover =
    match === null &&
    input.roots.some(
      root => isHomeRelativePath(root.path) && root.mode === "read_write"
    );
  return match?.mode === "read_write" || homeMayCover
    ? { visible: true, enabled: true, reason: null }
    : {
        visible: true,
        enabled: false,
        reason: WRITABLE_TOGGLE_REASONS.readOnlyFolder,
      };
};
